import type {ServiceAPIEntry, ServiceAPIRegistry, ServiceHandler, ServiceHandlerType} from './types'

export interface Orchestrator {
    contributeIsRunningInTransaction(fn: () => boolean): void
    wrapServiceAPIEntry(entry: ServiceAPIEntry): ServiceAPIRegistry
}

export const createOrchestrator = (): Orchestrator => {
    let _isRunningInTransaction: (() => boolean) | undefined

    const executeQuery =
        (serviceKey: string, methodName: string, handler: (...args: any[]) => any): ((...args: any[]) => any) =>
        (...args) =>
            handler(...args)

    const executeAction =
        (serviceKey: string, methodName: string, handler: (...args: any[]) => any): ((...args: any[]) => any) =>
        (...args) =>
            handler(...args)

    const executeDataManipulation =
        (serviceKey: string, methodName: string, handler: (...args: any[]) => any): ((...args: any[]) => any) =>
        (...args) => {
            if (!_isRunningInTransaction) {
                throw new Error('should contribute isRunningInTransaction')
            }
            if (!_isRunningInTransaction()) {
                throw new Error(`the public api \`${serviceKey}.${methodName}\` should run inside a transaction`)
            }
            return handler(...args)
        }

    const executeQueuedDataManipulation =
        (
            serviceKey: string,
            methodName: string,
            handler: (...args: any[]) => any
        ): ((...args: any[]) => Promise<any>) =>
        (...args) =>
            Promise.resolve(handler(...args))

    const wrappersByType: Record<
        ServiceHandlerType,
        (serviceKey: string, methodName: string, handler: (...args: any[]) => any) => (...args: any[]) => any
    > = {
        query: executeQuery,
        action: executeAction,
        dataManipulation: executeDataManipulation,
        queuedDataManipulation: executeQueuedDataManipulation
    }

    const contributeIsRunningInTransaction = (fn: () => boolean): void => {
        _isRunningInTransaction = fn
    }

    const wrapServiceAPIEntry = ({handlers, serviceKey}: ServiceAPIEntry): ServiceAPIRegistry => {
        const result: ServiceAPIRegistry = {}
        result[serviceKey] = {}
        for (const [methodName, {handler, options}] of Object.entries(handlers) as [string, ServiceHandler][]) {
            result[serviceKey][methodName] = wrappersByType[options.type](serviceKey, methodName, handler)
        }
        return result
    }

    return {contributeIsRunningInTransaction, wrapServiceAPIEntry}
}
